import { useState, useEffect } from "react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Stack,
  NumberInput,
  SegmentedControl,
  Button,
  Text,
  Loader,
  rem,
  ThemeIcon,
  Group,
  Progress,
  Card,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconUsers,
  IconSparkles,
  IconAlertTriangle,
  IconCheck,
  IconShieldOff,
} from "@tabler/icons-react";
import { isNumber } from "lodash";
import { Tournament, TournamentStep } from "../../../../../gql/graphql";

type TeamCountMode = "confirmed" | "custom";

const TEAM_COUNT_MODES: readonly TeamCountMode[] = ["confirmed", "custom"];

const isTeamCountMode = (value: string): value is TeamCountMode =>
  (TEAM_COUNT_MODES as readonly string[]).includes(value);

const GENERATE_STEP_MODAL_ID = "generate-step-modal";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(200),
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  teamCountCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
  },
  inputWrapper: {
    marginBottom: theme.spacing.xs,
  },
  warningText: {
    backgroundColor: theme.colors.rose[9] + "20",
    border: `1px solid ${theme.colors.rose[7]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.sm,
    marginTop: theme.spacing.md,
  },
  generateButton: {
    fontWeight: 600,
    marginTop: theme.spacing.md,
  },
  progressSection: {
    marginTop: theme.spacing.md,
  },
}));

interface GenerateStepModalProps {
  tournamentId: string;
  stepId: string;
  onSuccess?: () => void;
}

const STEP_QUERY = gql`
  query ($tournamentId: ID!, $stepId: ID!) {
    tournamentStep(id: $stepId) {
      id
      name
      order
      status
      type
    }
    stepRuleSet(tournamentStepId: $stepId) {
      id
      validated
      validatedAt
      teamCount
    }
    tournament(id: $tournamentId) {
      configuration {
        teamsCount
      }
      teams(status: CONFIRMED, page: {}) {
        totalCount
      }
    }
  }
`;

const GENERATE_STEP_MUTATION = gql`
  mutation ($stepId: ID!, $teamsCount: Int) {
    generateTournamentStep(stepId: $stepId, teamsCount: $teamsCount)
  }
`;

export const GenerateStepModal = ({
  tournamentId,
  stepId,
  onSuccess,
}: GenerateStepModalProps) => {
  const { classes } = useStyles();
  const [teamCountMode, setTeamCountMode] =
    useState<TeamCountMode>("confirmed");
  const [customTeamsCount, setCustomTeamsCount] = useState<
    number | undefined
  >();
  const [generating, setGenerating] = useState(false);

  const { data, loading } = useQuery<{
    tournamentStep: TournamentStep;
    stepRuleSet: {
      id: string;
      validated: boolean;
      validatedAt: string;
      teamCount: number | null;
    } | null;
    tournament: Tournament;
  }>(STEP_QUERY, {
    variables: {
      tournamentId,
      stepId,
    },
    fetchPolicy: "network-only",
  });

  const [generateStep] = useMutation(GENERATE_STEP_MUTATION);

  const ruleSet = data?.stepRuleSet;
  const hasRuleSet = !!ruleSet;
  const isValidated = ruleSet?.validated === true;
  const ruleSetTeamCount = ruleSet?.teamCount ?? null;

  const confirmedTeamsCount = data?.tournament?.teams?.totalCount ?? 0;

  useEffect(() => {
    if (ruleSetTeamCount) {
      setTeamCountMode("custom");
      setCustomTeamsCount(ruleSetTeamCount);
    }
  }, [ruleSetTeamCount]);

  const teamsCount =
    teamCountMode === "confirmed" ? confirmedTeamsCount : customTeamsCount;
  const isOverLimit =
    (teamsCount ?? 0) > confirmedTeamsCount && confirmedTeamsCount > 0;
  const progressPercent = confirmedTeamsCount
    ? Math.min(((teamsCount ?? 0) / confirmedTeamsCount) * 100, 100)
    : 0;

  const handleGenerate = async () => {
    if (teamsCount === undefined) return;

    setGenerating(true);
    try {
      const result = await generateStep({
        variables: {
          stepId,
          teamsCount,
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Step Generated",
        message: `Successfully generated step with ${teamsCount} teams`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onSuccess?.();
      modals.close(GENERATE_STEP_MODAL_ID);
    } catch (error) {
      notifications.show({
        title: "Generation Failed",
        message:
          error instanceof Error ? error.message : "Failed to generate step",
        color: "rose",
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Stack align="center" justify="center" className={classes.container}>
        <Loader color="cyan" size="lg" />
        <Text c="dimmed" size="sm">
          Loading tournament data...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack className={classes.container}>
      {/* Header */}
      <Group className={classes.header}>
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconSparkles size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600}>Generate Tournament Step</Text>
          <Text size="xs" c="dimmed">
            Configure the number of teams for this step
          </Text>
        </div>
      </Group>

      {/* Team Count Card */}
      <Card className={classes.teamCountCard}>
        <Stack gap="xs">
          <Group justify="space-between">
            <Group gap="xs">
              <IconUsers size={16} />
              <Text size="sm" fw={500}>
                Confirmed Teams
              </Text>
            </Group>
            <Text fw={600} c="cyan">
              {confirmedTeamsCount}
            </Text>
          </Group>

          <SegmentedControl
            value={teamCountMode}
            onChange={(value) => {
              if (isTeamCountMode(value)) {
                setTeamCountMode(value);
              }
            }}
            data={[
              {
                label: `Use Confirmed (${confirmedTeamsCount})`,
                value: "confirmed",
              },
              { label: "Custom", value: "custom" },
            ]}
            fullWidth
          />

          {teamCountMode === "custom" && (
            <div className={classes.inputWrapper}>
              <NumberInput
                value={customTeamsCount}
                min={0}
                onChange={(value) => {
                  setCustomTeamsCount(isNumber(value) ? value : undefined);
                }}
                placeholder="Number of teams in this step"
                label="Teams Count"
                description={
                  ruleSetTeamCount
                    ? `Rule set configured: ${ruleSetTeamCount} teams`
                    : "How many teams should participate in this step"
                }
              />
            </div>
          )}

          {/* Progress indicator */}
          {teamCountMode === "custom" &&
            customTeamsCount !== undefined &&
            confirmedTeamsCount > 0 && (
              <div className={classes.progressSection}>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="dimmed">
                    Teams selected
                  </Text>
                  <Text size="xs" c="dimmed">
                    {customTeamsCount} / {confirmedTeamsCount}
                  </Text>
                </Group>
                <Progress
                  value={progressPercent}
                  color={isOverLimit ? "rose" : "cyan"}
                  size="sm"
                  radius="xl"
                />
              </div>
            )}
        </Stack>
      </Card>

      {/* Warning for over-limit */}
      {isOverLimit && (
        <Group className={classes.warningText} gap="sm">
          <IconAlertTriangle size={18} color="#f43f5e" />
          <Text size="sm" c="rose">
            You are trying to generate with more teams than confirmed. This will
            create empty game slots.
          </Text>
        </Group>
      )}

      {/* Validation Gate Warning */}
      {hasRuleSet && !isValidated && (
        <Group className={classes.warningText} gap="sm">
          <IconShieldOff size={18} />
          <Stack gap={2}>
            <Text size="sm" fw={500} c="yellow">
              Rules must be validated before generating
            </Text>
            <Text size="xs" c="dimmed">
              Run a successful simulation on the Rules page to validate your
              rule set.
            </Text>
          </Stack>
        </Group>
      )}

      {/* Generate Button */}
      <Button
        className={classes.generateButton}
        disabled={
          teamsCount === undefined ||
          teamsCount === 0 ||
          (hasRuleSet && !isValidated)
        }
        loading={generating}
        onClick={handleGenerate}
        leftSection={<IconSparkles size={16} />}
        fullWidth
      >
        Generate Step
      </Button>
    </Stack>
  );
};

// Helper to open the modal
export const openGenerateStepModal = (props: GenerateStepModalProps) => {
  modals.open({
    modalId: GENERATE_STEP_MODAL_ID,
    title: null,
    centered: true,
    size: "md",
    children: <GenerateStepModal {...props} />,
  });
};
